import { useEffect, useState } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Target, Plus, Trash2, AlertTriangle, Flag, Archive, RefreshCw,
  BookOpen, GraduationCap, Sliders, Clock, Brain, Layers,
  CheckCircle2, CalendarDays, Sparkles, Search, TrendingUp,
  ChevronLeft, ChevronRight, Check, Monitor, MapPin, Zap,
  MessageCircle, RefreshCw as RefreshCwIcon,
} from 'lucide-react';
import { StudentLayout } from '@/components/student/layout/StudentLayout';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { FieldHint } from '@/components/help/FieldHint';
import { Slider } from '@/components/ui/slider';
import myGoalHelp from '@/content/help/my-goal-help.json';
import { fetchSubjects } from '@/services/AdminFetchServices';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useGoalProfile, useUpsertGoalProfile } from '@/hooks/useGoalProfile';
import { useExamTargets } from '@/hooks/useExamTargets';
import { useEnrichment } from '@/hooks/useEnrichment';
import { daysUntil, ExamTargetError, MAX_EXAM_TARGETS } from '@/services/examTargetService';
import {
  TIMELINE_OPTIONS,
  goalTypesForGrade, modeLabel, timelineLabel, gradeLabel,
  LEARNING_STYLES, DAYS, TIMES, learningStyleLabel,
} from '@/lib/goalOptions';
import { useExamCatalog } from '@/hooks/useExamCatalog';
import { useGradeBands, useCurricula, useTeachingModes } from '@/hooks/useValueCatalog';
import { useStudentProfile } from '@/contexts/StudentProfileContext';
import { currencySymbol } from '@/lib/currency';
import { useGeoDefaults } from '@/hooks/useGeoDefaults';
import { useAuth } from '@/hooks/use-auth-compat';
import type { GoalProfileRequest, GoalProfileResponse } from '@/types/goalProfile';
import type { AvailabilitySlot, LearningStyle } from '@/types/registeredStudent';

const todayIso = () => new Date().toISOString().slice(0, 10);

const steps = [
  { id: 'grade',        label: 'Grade',       icon: GraduationCap, description: 'Grade level & curriculum' },
  { id: 'subject',      label: 'Subject',     icon: BookOpen,      description: 'What you want to learn' },
  { id: 'exams',        label: 'Exams',       icon: Flag,          description: 'Exam targets & deadlines' },
  { id: 'preferences',  label: 'Preferences', icon: Sliders,       description: 'Mode, budget & timeline' },
  { id: 'availability', label: 'Schedule',    icon: Clock,         description: 'When you can study' },
  { id: 'learning',     label: 'Style',       icon: Brain,         description: 'How you like to learn' },
];

// ── Icon card (replaces Chip for visual selections) ──
function IconCard({
  active,
  onClick,
  icon: Icon,
  label,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 px-3 py-3 transition-all duration-150 cursor-pointer',
        'w-[100px] min-h-[72px] text-center shrink-0',
        active
          ? 'border-primary bg-primary/10 shadow-sm'
          : 'border-border bg-background text-foreground hover:border-primary/40 hover:bg-primary/5'
      )}
    >
      <Icon className={cn('w-5 h-5 shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
      <span className={cn('text-xs font-medium leading-tight', active ? 'text-primary' : 'text-foreground')}>{label}</span>
    </button>
  );
}

// Fallback plain Chip (kept for availability grid usage)
function Chip({ active, onClick, disabled, children }: { active: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 cursor-pointer',
        active
          ? 'bg-primary text-primary-foreground border-primary shadow-sm'
          : 'bg-background text-foreground border-border hover:border-primary/40 hover:bg-primary/5'
      )}
    >
      {children}
    </button>
  );
}

function budgetText(min: number | null | undefined, max: number | null | undefined, currency: string | null | undefined): string {
  // SCRUM-365: backend currency only — no literal rupee fallback.
  const sym = currencySymbol(currency);
  if (min && max) return `${sym}${min}–${sym}${max}`;
  if (max) return `up to ${sym}${max}`;
  if (min) return `from ${sym}${min}`;
  return '';
}

// ── Step section header ──
function StepHeader({ icon: Icon, heading, subtext }: { icon: React.ElementType; heading: string; subtext: string }) {
  return (
    <div className="flex items-start gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="text-base font-semibold text-foreground leading-tight">{heading}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{subtext}</p>
      </div>
    </div>
  );
}

// ── Goal icon for goal types ──
function goalTypeIcon(gt: string): React.ElementType {
  const lower = gt.toLowerCase();
  if (lower.includes('homework')) return BookOpen;
  if (lower.includes('board exam')) return GraduationCap;
  if (lower.includes('jee') || lower.includes('neet') || lower.includes('competitive')) return TrendingUp;
  if (lower.includes('concept') || lower.includes('catch-up') || lower.includes('tuition')) return Layers;
  if (lower.includes('scholarship')) return Sparkles;
  if (lower.includes('upskill') || lower.includes('career')) return TrendingUp;
  return Target;
}

export default function MyGoalPage() {
  usePageTitle('My Learning Goal | BookMyCoaching');
  const { data: goal, isLoading } = useGoalProfile();
  const { profile: studentProfile } = useStudentProfile();
  const budgetCurrencySymbol = currencySymbol(studentProfile?.currencyCode);
  const upsert = useUpsertGoalProfile();
  const exams = useExamTargets();
  const enrichment = useEnrichment();
  const { exams: catalogExams } = useExamCatalog();
  const gradeBands = useGradeBands();
  const curricula = useCurricula();
  const teachingModes = useTeachingModes();

  // geo defaults for budget slider
  const { user } = useAuth();
  const accessToken = user?.access_token ?? null;
  const { data: geoDefaults } = useGeoDefaults(accessToken);

  const [currentStep, setCurrentStep] = useState(0);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [showWizard, setShowWizard] = useState(false);

  // form state
  const [subject, setSubject] = useState('');
  const [gradeBand, setGradeBand] = useState('');
  const [curriculum, setCurriculum] = useState('');
  const [goalType, setGoalType] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [mode, setMode] = useState('');
  const [timeline, setTimeline] = useState('');

  // exam form
  const [showExamForm, setShowExamForm] = useState(false);
  const [examId, setExamId] = useState('');
  const [otherExamName, setOtherExamName] = useState('');
  const [examDate, setExamDate] = useState('');
  const [targetScore, setTargetScore] = useState('');

  // enrichment
  const [availDraft, setAvailDraft] = useState<AvailabilitySlot[]>([]);
  const [styleDraft, setStyleDraft] = useState<LearningStyle | null>(null);

  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<GoalProfileRequest | null>(null);
  const [saving, setSaving] = useState(false);

  const slotOn = (slots: AvailabilitySlot[], day: string, time: string) =>
    slots.some((s) => s.day === day && s.time === time);
  const toggleSlot = (day: string, time: string) =>
    setAvailDraft((prev) =>
      slotOn(prev, day, time)
        ? prev.filter((s) => !(s.day === day && s.time === time))
        : [...prev, { day: day as AvailabilitySlot['day'], time: time as AvailabilitySlot['time'] }]
    );

  useEffect(() => {
    fetchSubjects()
      .then((d: Array<{ name: string }> | null) => { if (Array.isArray(d)) setSubjects(d.map((s) => s.name)); }).catch(() => {});
  }, []);

  // Sync form from saved goal when it loads
  useEffect(() => {
    if (goal) {
      setSubject(goal.subject ?? '');
      setGradeBand(goal.gradeBand ?? '');
      setCurriculum(goal.curriculum ?? '');
      setGoalType(goal.goalType ?? '');
      setBudgetMin(goal.budgetMin != null ? String(goal.budgetMin) : '');
      setBudgetMax(goal.budgetMax != null ? String(goal.budgetMax) : '');
      setMode(goal.modePreference ?? '');
      setTimeline(goal.startTimeline ?? '');
    }
  }, [goal]);

  useEffect(() => {
    setAvailDraft(enrichment.availability);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichment.availability.length]);

  useEffect(() => {
    setStyleDraft(enrichment.prefs.learningStyle ?? null);
  }, [enrichment.prefs.learningStyle]);

  const buildRequest = (): GoalProfileRequest => ({
    subject: subject || undefined,
    gradeBand: gradeBand || undefined,
    curriculum: curriculum || undefined,
    goalType: goalType || undefined,
    budgetMin: budgetMin ? parseInt(budgetMin, 10) : undefined,
    budgetMax: budgetMax ? parseInt(budgetMax, 10) : undefined,
    modePreference: mode || undefined,
    startTimeline: timeline || undefined,
  });

  const isSignificant = (req: GoalProfileRequest) =>
    !!goal && ((req.subject ?? '') !== (goal.subject ?? '') || (req.gradeBand ?? '') !== (goal.gradeBand ?? ''));

  async function saveCurrentStep() {
    const req = buildRequest();
    const stepId = steps[currentStep].id;

    if ((stepId === 'grade' || stepId === 'subject') && isSignificant(req)) {
      setPendingSave(req); setConsentOpen(true); return;
    }

    if (stepId === 'availability') {
      try {
        setSaving(true);
        await enrichment.saveAvailability(availDraft);
        toast.success('Schedule saved');
      } catch { toast.error('Could not save schedule.'); }
      finally { setSaving(false); }
      return;
    }

    if (stepId === 'learning') {
      try {
        setSaving(true);
        await enrichment.saveLearningStyle(styleDraft);
        toast.success('Learning style saved');
      } catch { toast.error('Could not save learning style.'); }
      finally { setSaving(false); }
      return;
    }

    if (stepId === 'exams') return; // exams are saved inline

    try {
      setSaving(true);
      await upsert.mutateAsync({ ...req, notifyTeachers: false });
      toast.success('Saved');
    } catch {
      toast.error('Could not save. Please try again.');
    } finally { setSaving(false); }
  }

  async function commitSave(req: GoalProfileRequest, notify?: boolean) {
    try {
      setSaving(true);
      await upsert.mutateAsync({ ...req, notifyTeachers: notify ?? false });
      toast.success('Goal saved' + (notify ? ' · teachers notified' : ''));
    } catch {
      toast.error('Could not save your goal. Please try again.');
    } finally {
      setSaving(false);
      setConsentOpen(false);
      setPendingSave(null);
    }
  }

  async function handleNext() {
    await saveCurrentStep();
    if (currentStep < steps.length - 1) setCurrentStep((s) => s + 1);
  }

  async function onAddExam() {
    try {
      if (examId === '__other__') {
        if (!otherExamName.trim()) { toast.error('Enter an exam name.'); return; }
        await exams.add({ examName: otherExamName.trim(), examDate, targetScore: targetScore || undefined });
      } else {
        const exam = catalogExams.find((e) => e.id === examId);
        if (!exam) { toast.error('Choose an exam.'); return; }
        await exams.add({ examName: exam.name, catalogExamId: exam.id, examDate, targetScore: targetScore || undefined });
      }
      setShowExamForm(false); setExamId(''); setOtherExamName(''); setExamDate(''); setTargetScore('');
      toast.success('Exam target added');
    } catch (e) {
      toast.error(e instanceof ExamTargetError ? e.message : 'Could not add exam target.');
    }
  }

  // Step completion status
  const stepCompleted = [
    !!goal?.gradeBand,
    !!goal?.subject,
    exams.targets.length > 0,
    !!(goal?.modePreference || goal?.budgetMax || goal?.startTimeline),
    enrichment.availability.length > 0,
    !!enrichment.prefs.learningStyle,
  ];
  const completionCount = stepCompleted.filter(Boolean).length;
  const completionPct = Math.round((completionCount / steps.length) * 100);

  // ── Budget slider derived values ──
  const geoMin = geoDefaults?.minPrice ?? 100;
  const geoMax = geoDefaults?.maxPrice ?? 5000;
  const parsedMin = budgetMin !== '' ? parseInt(budgetMin, 10) : NaN;
  const parsedMax = budgetMax !== '' ? parseInt(budgetMax, 10) : NaN;
  // Clamp to [geoMin, geoMax] and fall back to range ends when empty.
  // Do NOT use `|| geoMin` — parseInt("0") === 0 which is falsy and would snap back.
  const sliderMin = isNaN(parsedMin) ? geoMin : Math.max(geoMin, Math.min(geoMax - 1, parsedMin));
  const sliderMax = isNaN(parsedMax) ? geoMax : Math.max(geoMin + 1, Math.min(geoMax, parsedMax));

  // ── Step renderers ──

  const renderGrade = () => (
    <div className="space-y-5">
      <StepHeader
        icon={GraduationCap}
        heading="Your Grade & Curriculum"
        subtext="Helps us match you with teachers for your level"
      />
      <div>
        <p className="text-sm font-medium text-foreground mb-3">
          What grade level are you in? <FieldHint content={myGoalHelp} stepId="grade" fieldKey="gradeBand" />
        </p>
        {gradeBands.isError ? (
          <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
        ) : (
          <SearchableSelect
            options={gradeBands.entries.map((g) => ({ label: g.label, value: g.code }))}
            value={gradeBand}
            onChange={setGradeBand}
            placeholder="Search and select your grade…"
            searchPlaceholder="Search grade levels…"
          />
        )}
      </div>
      {curricula.isError ? (
        <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
      ) : curricula.entries.length > 0 && (
        <div>
          <p className="text-sm font-medium text-foreground mb-3">
            Which curriculum do you follow? <FieldHint content={myGoalHelp} stepId="grade" fieldKey="curriculum" />
          </p>
          <SearchableSelect
            options={curricula.entries.map((c) => ({ label: c.label, value: c.code }))}
            value={curriculum}
            onChange={setCurriculum}
            placeholder="Search and select your curriculum…"
            searchPlaceholder="Search curricula…"
          />
        </div>
      )}
    </div>
  );

  const renderSubject = () => (
    <div className="space-y-5">
      <StepHeader
        icon={BookOpen}
        heading="Subject & Learning Goal"
        subtext="We'll surface teachers who specialise in your subject and goal type"
      />
      <div>
        <p className="text-sm font-medium text-foreground mb-3">What subject do you want to study?</p>
        <SearchableSelect
          options={subjects}
          value={subject}
          onChange={setSubject}
          placeholder="Search and select a subject…"
          searchPlaceholder="Search subjects…"
        />
      </div>
      {goalTypesForGrade(gradeBand).length > 0 && (
        <div>
          <p className="text-sm font-medium text-foreground mb-3">
            What's your learning goal? <FieldHint content={myGoalHelp} stepId="subject" fieldKey="goalType" />
          </p>
          <div className="flex flex-wrap gap-2">
            {goalTypesForGrade(gradeBand).map((gt) => (
              <IconCard
                key={gt}
                icon={goalTypeIcon(gt)}
                label={gt}
                active={goalType === gt}
                onClick={() => setGoalType((p) => p === gt ? '' : gt)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderExams = () => (
    <div className="space-y-3">
      <StepHeader
        icon={Flag}
        heading="Exam Targets"
        subtext="We'll show you a countdown and help you prepare with the right teachers"
      />
      {exams.targets.length === 0 && !showExamForm && (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-6 text-center">
          <Flag className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground mb-3">No exam targets set yet</p>
          <Button size="sm" variant="outline" onClick={() => setShowExamForm(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Add exam target
          </Button>
        </div>
      )}
      {exams.targets.map((t) => {
        const d = daysUntil(t.examDate);
        const urgent = d >= 0 && d <= 30;
        const past = d < 0;
        return (
          <div key={t.id} className={cn(
            'flex items-center gap-3 rounded-xl border px-4 py-3',
            urgent ? 'bg-amber-50 border-amber-200' : 'bg-muted/30 border-border'
          )}>
            <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', urgent ? 'bg-amber-100' : 'bg-muted')}>
              <CalendarDays className={cn('w-4 h-4', urgent ? 'text-amber-600' : 'text-muted-foreground')} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">{t.examName}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {new Date(t.examDate).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}
                {t.targetScore && ` · Target: ${t.targetScore}`}
              </p>
            </div>
            <div className="shrink-0 text-right mr-2">
              {!past && <p className={cn('text-sm font-bold', urgent ? 'text-amber-600' : 'text-foreground')}>{d}d</p>}
              <p className="text-xs text-muted-foreground">{past ? 'Past' : 'to go'}</p>
            </div>
            <button onClick={() => exams.remove(t.id)} className="text-muted-foreground/40 hover:text-destructive transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      })}
      {exams.targets.length > 0 && exams.targets.length < MAX_EXAM_TARGETS && !showExamForm && (
        <button onClick={() => setShowExamForm(true)} className="text-sm text-primary hover:text-primary/80 flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Add another exam
        </button>
      )}
      {showExamForm && (
        <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Exam <span className="text-destructive">*</span></label>
              <select value={examId} onChange={(e) => setExamId(e.target.value)} className="w-full h-10 px-3 text-sm border border-border rounded-lg bg-background">
                <option value="">Select an exam…</option>
                {catalogExams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                <option value="__other__">Other (not listed)</option>
              </select>
              {examId === '__other__' && (
                <input
                  value={otherExamName}
                  onChange={(e) => setOtherExamName(e.target.value)}
                  placeholder="Enter exam name"
                  className="mt-2 w-full h-10 px-3 text-sm border border-border rounded-lg bg-background"
                />
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Exam date <span className="text-destructive">*</span>{' '}
                <FieldHint content={myGoalHelp} stepId="exams" fieldKey="examDate" />
              </label>
              <input type="date" min={todayIso()} value={examDate} onChange={(e) => setExamDate(e.target.value)} className="w-full h-10 px-3 text-sm border border-border rounded-lg bg-background" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Target score <span className="text-xs text-muted-foreground font-normal">(optional)</span>{' '}
              <FieldHint content={myGoalHelp} stepId="exams" fieldKey="targetScore" />
            </label>
            <input value={targetScore} onChange={(e) => setTargetScore(e.target.value)} placeholder="e.g. 99 percentile" className="w-full h-10 px-3 text-sm border border-border rounded-lg bg-background" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={onAddExam}>Add exam</Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowExamForm(false); setExamId(''); setOtherExamName(''); setExamDate(''); setTargetScore(''); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );

  const modeIconMap: Record<string, React.ElementType> = {
    ONLINE: Monitor,
    OFFLINE: MapPin,
    HYBRID: Layers,
  };

  const timelineIconMap: Record<string, React.ElementType> = {
    THIS_WEEK: Zap,
    THIS_MONTH: CalendarDays,
    LATER: Clock,
  };

  const renderPreferences = () => (
    <div className="space-y-6">
      <StepHeader
        icon={Sliders}
        heading="Mode, Budget & Timeline"
        subtext="Narrow your teacher matches to fit your schedule and budget"
      />

      {/* Budget slider */}
      <div>
        <p className="text-sm font-medium text-foreground mb-1">
          Budget per session ({budgetCurrencySymbol}) <FieldHint content={myGoalHelp} stepId="preferences" fieldKey="budget" />
        </p>
        <p className="text-sm font-bold text-primary mb-3">
          {budgetCurrencySymbol}{sliderMin} – {budgetCurrencySymbol}{sliderMax}
        </p>
        <Slider
          value={[sliderMin, sliderMax]}
          min={geoMin}
          max={geoMax}
          step={50}
          minStepsBetweenThumbs={1}
          onValueChange={(vals: number[]) => {
            setBudgetMin(String(vals[0]));
            setBudgetMax(String(vals[1]));
          }}
          className="mt-2 mb-1"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{budgetCurrencySymbol}{geoMin}</span>
          <span>{budgetCurrencySymbol}{geoMax}</span>
        </div>
      </div>

      {/* Teaching mode icon cards */}
      <div>
        <p className="text-sm font-medium text-foreground mb-3">
          How do you prefer to learn? <FieldHint content={myGoalHelp} stepId="preferences" fieldKey="mode" />
        </p>
        <div className="flex flex-wrap gap-2">
          {teachingModes.entries.map((m) => (
            <IconCard
              key={m.code}
              icon={modeIconMap[m.code] ?? Monitor}
              label={m.label}
              active={mode === m.code}
              onClick={() => setMode((p) => p === m.code ? '' : m.code)}
            />
          ))}
        </div>
      </div>

      {/* Timeline icon cards */}
      <div>
        <p className="text-sm font-medium text-foreground mb-3">
          When do you want to start? <FieldHint content={myGoalHelp} stepId="preferences" fieldKey="timeline" />
        </p>
        <div className="flex flex-wrap gap-2">
          {TIMELINE_OPTIONS.map((t) => (
            <IconCard
              key={t.value}
              icon={timelineIconMap[t.value] ?? Clock}
              label={t.label}
              active={timeline === t.value}
              onClick={() => setTimeline((p) => p === t.value ? '' : t.value)}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderAvailability = () => (
    <div className="space-y-4">
      <StepHeader
        icon={Clock}
        heading="Your Schedule"
        subtext="Teachers can see when you're free and suggest sessions accordingly"
      />
      <p className="text-sm text-muted-foreground">Select the days and times you're generally free for sessions.</p>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="text-xs border-collapse w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="px-4 py-2.5 text-left text-muted-foreground font-medium"></th>
              {DAYS.map((d) => <th key={d.value} className="px-2 py-2.5 text-muted-foreground font-medium text-center min-w-[56px]">{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {TIMES.map((tm) => (
              <tr key={tm.value} className="border-t border-border">
                <td className="px-4 py-2 text-muted-foreground whitespace-nowrap font-medium">{tm.label}</td>
                {DAYS.map((d) => {
                  const on = slotOn(availDraft, d.value, tm.value);
                  return (
                    <td key={d.value} className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => toggleSlot(d.value, tm.value)}
                        className={cn(
                          'w-8 h-8 rounded-lg border transition-all duration-150 text-xs font-bold',
                          on ? 'bg-primary border-primary text-primary-foreground' : 'border-border hover:border-primary/40 hover:bg-primary/5'
                        )}
                      >
                        {on ? '✓' : ''}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {availDraft.length > 0 && (
        <p className="text-sm text-primary font-medium">{availDraft.length} slot{availDraft.length !== 1 ? 's' : ''} selected</p>
      )}
      {availDraft.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Select at least one slot so teachers know when you're free
        </div>
      )}
    </div>
  );

  const learningStyleIconMap: Record<string, React.ElementType> = {
    STRUCTURED: Layers,
    CONCEPT_FIRST: Brain,
    PRACTICE_HEAVY: Target,
    ADAPTIVE: RefreshCwIcon,
    CONVERSATIONAL: MessageCircle,
  };

  const renderLearningStyle = () => (
    <div className="space-y-5">
      <StepHeader
        icon={Brain}
        heading="Your Learning Style"
        subtext="Helps us match you with teachers who teach the way you learn"
      />
      <div>
        <p className="text-sm font-medium text-foreground mb-3">How do you learn best?</p>
        <div className="flex flex-wrap gap-2">
          {LEARNING_STYLES.map((s) => (
            <IconCard
              key={s.value}
              icon={learningStyleIconMap[s.value] ?? Brain}
              label={s.label}
              active={styleDraft === s.value}
              onClick={() => setStyleDraft((p) => p === s.value ? null : s.value as LearningStyle)}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const stepContent = [renderGrade, renderSubject, renderExams, renderPreferences, renderAvailability, renderLearningStyle];

  const summaryBudget = budgetText(goal?.budgetMin, goal?.budgetMax, goal?.currency);

  const isEditing = showWizard || !goal;

  return (
    <StudentLayout>
      <>
        <main className="flex-1 overflow-auto">
          <div className="max-w-6xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-5">

            {/* ── Loading skeleton ── */}
            {isLoading && (
              <div className="rounded-2xl border border-border bg-card shadow-sm h-64 animate-pulse" />
            )}

            {/* ── Empty state — no goal yet ── */}
            {!isLoading && !goal && !showWizard && (
              <div className="rounded-2xl border border-border bg-card shadow-sm px-6 py-14 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Set your learning goal</h1>
                <p className="text-sm text-muted-foreground max-w-sm mb-8 leading-relaxed">
                  Tell us what you want to learn and how you prefer to study. We'll match you with the right teachers.
                </p>
                <div className="flex flex-col sm:flex-row gap-5 mb-10">
                  {[
                    { icon: Search,       text: 'Better teacher matches' },
                    { icon: TrendingUp,   text: 'Personalised recommendations' },
                    { icon: CalendarDays, text: 'Exam countdown tracking' },
                  ].map(({ icon: I, text }) => (
                    <div key={text} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <I className="w-4 h-4 text-primary" />
                      </div>
                      {text}
                    </div>
                  ))}
                </div>
                <Button size="lg" onClick={() => setShowWizard(true)}>
                  Get started
                </Button>
              </div>
            )}

            {/* ── View mode — goal exists and not editing ── */}
            {!isLoading && goal && !showWizard && (
              <>
                {/* Hero view card */}
                <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
                  {/* Card header */}
                  <div className="relative px-6 py-6 bg-primary/5 dark:bg-primary/10 overflow-hidden">
                    <div className="absolute -top-6 -right-6 w-40 h-40 bg-indigo-400/10 rounded-full blur-2xl pointer-events-none" />
                    <div className="relative z-10 flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                          <span className="text-xs font-bold uppercase tracking-widest text-primary">Learning Profile</span>
                        </div>
                        <h1 className="text-2xl font-bold text-foreground mb-0.5">
                          {goal.subject ?? 'My Goal'}
                        </h1>
                        {goal.gradeBand && (
                          <p className="text-sm text-muted-foreground">{gradeLabel(goal.gradeBand)}{goal.curriculum ? ` · ${goal.curriculum}` : ''}</p>
                        )}
                        {goal.goalType && (
                          <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                            {goal.goalType}
                          </span>
                        )}
                      </div>
                      <Button variant="outline" size="sm" className="shrink-0" onClick={() => { setCurrentStep(0); setShowWizard(true); }}>
                        Edit goal
                      </Button>
                    </div>
                  </div>

                  {/* Details grid */}
                  <div className="px-6 py-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-5 border-t border-border">
                    {(() => {
                      // Exams: show next upcoming exam name + countdown, or list names if multiple
                      const upcomingExams = exams.targets
                        .map(t => ({ ...t, d: daysUntil(t.examDate) }))
                        .filter(t => t.d >= 0)
                        .sort((a, b) => a.d - b.d);
                      const examValue = upcomingExams.length > 0
                        ? upcomingExams.length === 1
                          ? `${upcomingExams[0].examName} · ${upcomingExams[0].d}d`
                          : upcomingExams.slice(0, 2).map(t => t.examName).join(' · ')
                        : exams.targets.length > 0
                          ? exams.targets.map(t => t.examName).join(' · ')
                          : null;

                      // Availability: derive unique days, show as "Mon · Wed · Fri" style
                      const dayOrder = ['MON','TUE','WED','THU','FRI','SAT','SUN'];
                      const dayShort: Record<string, string> = { MON:'Mon', TUE:'Tue', WED:'Wed', THU:'Thu', FRI:'Fri', SAT:'Sat', SUN:'Sun' };
                      const activeDays = dayOrder.filter(d => enrichment.availability.some(s => s.day === d));
                      const availValue = activeDays.length > 0 ? activeDays.map(d => dayShort[d]).join(' · ') : null;

                      return [
                        { icon: Flag,         label: 'Exams',          value: examValue },
                        { icon: Monitor,      label: 'Mode',           value: goal.modePreference ? modeLabel(goal.modePreference) : null },
                        { icon: Sliders,      label: 'Budget',         value: summaryBudget || null },
                        { icon: CalendarDays, label: 'Timeline',       value: goal.startTimeline ? timelineLabel(goal.startTimeline) : null },
                        { icon: Brain,        label: 'Learning style', value: enrichment.prefs.learningStyle ? learningStyleLabel(enrichment.prefs.learningStyle) : null },
                        { icon: Clock,        label: 'Availability',   value: availValue },
                      ].map(({ icon: I, label, value }) => (
                        <div key={label} className="flex items-start gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                            <I className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground">{label}</p>
                            <p className={cn('text-sm font-semibold truncate', value ? 'text-foreground' : 'text-muted-foreground/40')}>
                              {value ?? '—'}
                            </p>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Completion bar — inside the view card */}
                  <div className="px-6 py-4 border-t border-border flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-muted-foreground">Profile completeness</span>
                        <span className="text-xs font-bold text-primary">{completionPct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${completionPct}%` }} />
                      </div>
                    </div>
                    {completionPct === 100
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      : <button className="text-xs text-primary hover:underline shrink-0" onClick={() => { setCurrentStep(0); setShowWizard(true); }}>Complete profile</button>
                    }
                  </div>
                </div>

                {/* Exam targets — shown separately only when they exist */}
                {exams.targets.length > 0 && (
                  <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
                      <div className="flex items-center gap-2">
                        <Flag className="w-4 h-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">Exam Targets</span>
                      </div>
                      <button className="text-xs text-primary hover:underline" onClick={() => { setCurrentStep(2); setShowWizard(true); }}>Edit</button>
                    </div>
                    <div className="px-5 py-3 space-y-2">
                      {exams.targets.map((t) => {
                        const d = daysUntil(t.examDate);
                        const urgent = d >= 0 && d <= 30;
                        const past = d < 0;
                        return (
                          <div key={t.id} className={cn(
                            'flex items-center gap-3 rounded-xl px-4 py-3',
                            urgent ? 'bg-amber-50 border border-amber-200' : 'bg-muted/30'
                          )}>
                            <CalendarDays className={cn('w-4 h-4 shrink-0', urgent ? 'text-amber-600' : 'text-muted-foreground')} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground">{t.examName}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(t.examDate).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}
                                {t.targetScore && ` · Target: ${t.targetScore}`}
                              </p>
                            </div>
                            {!past && (
                              <p className={cn('text-sm font-bold shrink-0', urgent ? 'text-amber-600' : 'text-foreground')}>{d}d</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Edit mode — wizard ── */}
            {!isLoading && isEditing && (
              <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">

                {/* ── Dot stepper header ── */}
                <div className="px-6 pt-6 pb-5 border-b border-border">
                  {/* Step dots + connecting lines */}
                  <div className="flex items-center">
                    {steps.map((step, idx) => {
                      const isCompleted = stepCompleted[idx];
                      const isActive = idx === currentStep;
                      const Icon = step.icon;
                      return (
                        <div key={step.id} className="flex items-center flex-1 min-w-0">
                          <button
                            type="button"
                            onClick={() => setCurrentStep(idx)}
                            className="flex flex-col items-center gap-1.5 group shrink-0 focus:outline-none"
                            aria-current={isActive ? 'step' : undefined}
                          >
                            <div className={cn(
                              'w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ring-2 ring-offset-2',
                              isActive
                                ? 'bg-primary text-primary-foreground ring-primary shadow-md'
                                : isCompleted
                                ? 'bg-emerald-500 text-white ring-emerald-500'
                                : 'bg-muted text-muted-foreground ring-transparent group-hover:ring-primary/30'
                            )}>
                              {isCompleted && !isActive
                                ? <Check className="w-4 h-4" />
                                : <Icon className="w-4 h-4" />
                              }
                            </div>
                            {/* Label — always visible on sm+, only for active on xs */}
                            <span className={cn(
                              'text-[11px] font-semibold leading-tight text-center w-14 truncate transition-colors duration-200',
                              isActive ? 'text-primary' : isCompleted ? 'text-emerald-600' : 'text-muted-foreground',
                              !isActive && 'hidden sm:block',
                            )}>
                              {step.label}
                            </span>
                          </button>
                          {/* Connecting line */}
                          {idx < steps.length - 1 && (
                            <div className="flex-1 mx-2 mb-[18px] sm:mb-[22px]">
                              <div className={cn(
                                'h-0.5 w-full rounded-full transition-colors duration-300',
                                stepCompleted[idx] ? 'bg-emerald-400' : 'bg-border'
                              )} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Active step label + description (always shown below dots) */}
                  <div className="mt-4 flex items-start justify-between gap-4">
                    <div>
                      <p className="text-base font-semibold text-foreground leading-tight">{steps[currentStep].label}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{steps[currentStep].description}</p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0 mt-0.5">{currentStep + 1} / {steps.length}</span>
                  </div>
                </div>

                {/* Step content */}
                <div key={currentStep} className="px-4 sm:px-6 py-5 sm:py-6 animate-in fade-in-0 duration-200 min-h-[280px]">
                  {stepContent[currentStep]()}
                </div>

                {/* Footer */}
                <div className="px-4 sm:px-6 pb-5 pt-3 border-t border-border">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" onClick={() => setCurrentStep((s) => s - 1)} disabled={currentStep === 0} className="gap-2">
                        <ChevronLeft className="w-4 h-4" /> Previous
                      </Button>
                      {goal && (
                        <Button variant="ghost" onClick={() => setShowWizard(false)} className="text-muted-foreground">
                          Cancel
                        </Button>
                      )}
                    </div>
                    {currentStep < steps.length - 1 ? (
                      <Button onClick={handleNext} disabled={saving} className="gap-2">
                        Save & Next <ChevronRight className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button onClick={async () => { await saveCurrentStep(); setShowWizard(false); }} disabled={saving} className="gap-2">
                        <Check className="w-4 h-4" /> Save & done
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Changes update your match scores and recommendations immediately.</p>
                </div>

              </div>
            )}

          </div>
        </main>

        {/* ── Goal-change consent dialog ── */}
        <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>You're making a significant change</DialogTitle>
              <DialogDescription>
                You're changing your subject or grade. If you're in conversations with teachers, we can let them know your goal has changed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Archive className="w-4 h-4 shrink-0" /> Your current goal is archived to Goal History.</div>
              <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 shrink-0" /> Recommendations and saved-teacher matches will refresh.</div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => pendingSave && commitSave(pendingSave, false)}>Save quietly</Button>
              <Button onClick={() => pendingSave && commitSave(pendingSave, true)}>Notify teachers &amp; save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    </StudentLayout>
  );
}
